import { useMemo } from 'react';

import { Card, Form, Typography } from '@arco-design/web-react';

import { ServicePreviewSection } from '../../../capabilities/service/ServicePreviewSection';
import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import { isSkuCapableProfile } from '../../../platform/pipeline-kernel';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import type { BareMetalSpec } from '../headless/schema';
import { BARE_METAL_MODULES } from '../modules';

const PREVIEW_WATCH_FIELDS = [
  'name',
  'description',
  'namespace',
  'workspace',
  'datacenter',
  'powerPolicy',
  'bmsNode',
  'network',
  'systemVolume',
  'optionMeta',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '未配置';
const ALL_MODULE_KEYS = BARE_METAL_MODULES.map((mod) => mod.key);

function groupDescriptorsByModule(items: DisplayDescriptor[]): Map<string, DisplayDescriptor[]> {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

function partitionModuleItems(items: DisplayDescriptor[]) {
  const subsections: DisplayDescriptor[] = [];
  const flat: DisplayDescriptor[] = [];

  for (const item of items) {
    if (item.children?.length) {
      subsections.push(item);
    } else {
      flat.push(item);
    }
  }

  return { flat, subsections };
}

function renderModuleContent(modKey: string, items: DisplayDescriptor[], services: BareMetalSpec['network']['services']) {
  const { flat, subsections } = partitionModuleItems(items);
  const column = modKey === 'baseInfo' ? 2 : 1;

  if (modKey === 'service') {
    return <ServicePreviewSection services={services ?? []} />;
  }

  if (subsections.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {subsections.map((sub) => (
          <div key={sub.id}>
            <Typography.Text bold style={{ display: 'block', marginBottom: 8 }}>
              {sub.label}
            </Typography.Text>
            <DescriptorDescriptions items={sub.children!} column={1} />
          </div>
        ))}
      </div>
    );
  }

  const displayItems = flat.length ? flat : [{ id: `${modKey}-empty`, group: modKey, label: '-', value: PREVIEW_EMPTY }];
  return <DescriptorDescriptions items={displayItems} column={column} />;
}

export type BareMetalResourcePreviewProps = DeployResourcePreviewProps;

export function BareMetalResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: BareMetalResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('BareMetal');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const formValues = form?.getFieldsValue() ?? {};
  const skuResourceModules = formValues.skuResourceModules as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'BareMetal', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    namespace: ctx?.namespace ?? (formValues.namespace as string | undefined),
    cluster: ctx?.cluster ?? (formValues.datacenter as string | undefined),
    sku: { enabledModules: previewEnabledModules },
  };

  const resolvedSpec = (spec ?? plugin.pipeline.schema.parse(plugin.pipeline.fromForm(formValues, previewCtx))) as BareMetalSpec;

  const profile = plugin.pipeline.projectionProfile;
  const flatItems =
    descriptors ?? (isSkuCapableProfile(profile) ? profile.projections.buildDisplayDescriptors(resolvedSpec, previewCtx) : []);

  const grouped = groupDescriptorsByModule(flatItems);
  const services = resolvedSpec.network?.services;
  const visibleModules = BARE_METAL_MODULES.filter((mod) => enabledModuleKeys.includes(mod.key));

  if (!visibleModules.length) {
    return null;
  }

  return (
    <>
      {visibleModules.map((mod) => {
        const items = grouped.get(mod.key) ?? [{ id: `${mod.key}-empty`, group: mod.key, label: '-', value: PREVIEW_EMPTY }];

        return (
          <Card key={mod.key} bordered={false} title={mod.title} style={{ marginBottom: 12 }}>
            {renderModuleContent(mod.key, items, services)}
          </Card>
        );
      })}
    </>
  );
}
